import {defineTextStyles} from '@chakra-ui/react3'
import {breakpoints} from './breakpoints'

const chakraTextStyles = defineTextStyles({
    '2xs': {value: {fontSize: '2xs', lineHeight: '0.75rem'}},
    xs: {value: {fontSize: 'xs', lineHeight: '1rem'}},
    sm: {value: {fontSize: 'sm', lineHeight: '1.25rem'}},
    md: {value: {fontSize: 'md', lineHeight: '1.5rem'}},
    lg: {value: {fontSize: 'lg', lineHeight: '1.75rem'}},
    xl: {value: {fontSize: 'xl', lineHeight: '1.875rem'}},
    '2xl': {value: {fontSize: '2xl', lineHeight: '2rem'}},
    '3xl': {value: {fontSize: '3xl', lineHeight: '2.375rem'}},
    '4xl': {
        value: {
            fontSize: '4xl',
            lineHeight: '2.75rem',
            letterSpacing: '-0.025em'
        }
    },
    '5xl': {
        value: {
            fontSize: '5xl',
            lineHeight: '3.75rem',
            letterSpacing: '-0.025em'
        }
    },
    '6xl': {
        value: {fontSize: '6xl', lineHeight: '4.5rem', letterSpacing: '-0.025em'}
    },
    '7xl': {
        value: {
            fontSize: '7xl',
            lineHeight: '5.75rem',
            letterSpacing: '-0.025em'
        }
    },
    none: {
        value: {}
    },
    label: {
        value: {
            fontSize: 'sm',
            lineHeight: '1.25rem',
            fontWeight: 'medium'
        }
    }
})

// Breakpoint values in pixels (parsed from breakpoints)
const BREAKPOINT_MOBILE = parseInt(breakpoints.xxs) // 375
const BREAKPOINT_TABLET = parseInt(breakpoints.xs) // 768
const BREAKPOINT_DESKTOP = parseInt(breakpoints.m) // 1440

// Fluid typography helper that creates smooth transitions between breakpoints
// Uses linear interpolation formula: min + (max - min) * (100vw - minVw) / (maxVw - minVw)
// Values are converted to rem (16px = 1rem)
const createFluidValue = (minValue, maxValue, minVw, maxVw) => {
    const slope = (maxValue - minValue) / (maxVw - minVw)
    const intercept = minValue - slope * minVw
    const minRem = minValue / 16
    const maxRem = maxValue / 16
    const interceptRem = intercept / 16
    return `clamp(${minRem}rem, ${interceptRem.toFixed(4)}rem + ${(slope * 100).toFixed(
        4
    )}vw, ${maxRem}rem)`
}

// Typography helpers for smooth scaling across breakpoints
// Creates separate interpolations: mobile->tablet (xxs->xs) and tablet->desktop (xs->m)
// Returns responsive object with base (mobile-tablet) and xs (tablet-desktop) values
const fluidFontSize = (mobile, tablet, desktop) => ({
    base: createFluidValue(mobile, tablet, BREAKPOINT_MOBILE, BREAKPOINT_TABLET),
    xs: createFluidValue(tablet, desktop, BREAKPOINT_TABLET, BREAKPOINT_DESKTOP)
})

const fluidLineHeight = (mobile, tablet, desktop) => ({
    base: createFluidValue(mobile, tablet, BREAKPOINT_MOBILE, BREAKPOINT_TABLET),
    xs: createFluidValue(tablet, desktop, BREAKPOINT_TABLET, BREAKPOINT_DESKTOP)
})

const displayLarge = {
    fontFamily: 'Stokke',
    fontWeight: '300',
    // Mobile: 40px, Tablet: 56px, Desktop: 64px
    fontSize: fluidFontSize(40, 56, 64),
    // Mobile: 44px, Tablet: 60px, Desktop: 68px
    lineHeight: fluidLineHeight(44, 60, 68),
    letterSpacing: '-0.04em' // -4%
}

const labelMedium = {
    fontFamily: 'Stokke',
    fontWeight: '300', // Light
    fontSize: '0.75rem', // 12px
    lineHeight: '1rem', // 16px
    letterSpacing: '-0.01em' // -1%
}

export const textStyles = defineTextStyles({
    ...chakraTextStyles,
    display: {
        large: {
            value: {
                ...displayLarge
            }
        },
        largeItalic: {
            value: {
                ...displayLarge,
                fontStyle: 'italic'
            }
        },
        medium: {
            value: {
                fontFamily: 'Stokke',
                fontWeight: '300',
                // Mobile: 36px, Tablet: 40px, Desktop: 48px
                fontSize: fluidFontSize(32, 40, 48),
                // Mobile: 56px, Tablet: 48px, Desktop: 56px
                lineHeight: fluidLineHeight(40, 48, 56),
                letterSpacing: '-0.04em'
            }
        },
        small: {
            value: {
                fontFamily: 'Stokke',
                fontWeight: '300',
                // Mobile: 28px, Tablet: 32px, Desktop: 40px
                fontSize: fluidFontSize(28, 32, 40),
                // Mobile: 36px, Tablet: 40px, Desktop: 48px
                lineHeight: fluidLineHeight(36, 40, 48),
                letterSpacing: '-0.04em' // -4%
            }
        },
        xsmall: {
            value: {
                fontFamily: 'Stokke',
                fontWeight: '300',
                // Mobile: 24px, Tablet: 28px, Desktop: 32px
                fontSize: fluidFontSize(24, 28, 32),
                // Mobile: 32px, Tablet: 36px, Desktop: 40px
                lineHeight: fluidLineHeight(32, 36, 40),
                letterSpacing: '-0.04em' // -4%
            }
        }
    },
    heading: {
        xlarge: {
            value: {
                fontFamily: 'Stokke',
                fontWeight: '400', // Regular
                fontSize: '1.25rem', // 20px
                lineHeight: '1.75rem', // 28px
                letterSpacing: '-0.04em' // -4%
            }
        },
        large: {
            value: {
                fontFamily: 'Stokke',
                fontWeight: '400', // Regular
                fontSize: '1.125rem', // 18px
                lineHeight: '1.625rem', // 26px
                letterSpacing: '-0.03em' // -3%
            }
        },
        medium: {
            value: {
                fontFamily: 'Stokke',
                fontWeight: '400', // Regular
                fontSize: '1rem', // 16px
                lineHeight: '1.5rem', // 24px
                letterSpacing: '-0.03em' // -3%
            }
        },
        small: {
            value: {
                fontFamily: 'Stokke',
                fontWeight: '400', // Regular
                fontSize: '0.875rem', // 14px
                lineHeight: '1.25rem', // 20px
                letterSpacing: '-0.02em' // -2%
            }
        }
    },
    // body styles are not fluid
    body: {
        large: {
            value: {
                fontFamily: 'Stokke',
                fontWeight: '300', // Light
                fontSize: '1.125rem', // 18px
                lineHeight: '1.625rem', // 26px
                letterSpacing: '-0.03em' // -3%
            }
        },
        medium: {
            value: {
                fontFamily: 'Stokke',
                fontWeight: '300', // Light
                fontSize: '1rem', // 16px
                lineHeight: '1.5rem', // 24px
                letterSpacing: '-0.03em' // -3%
            }
        },
        small: {
            value: {
                fontFamily: 'Stokke',
                fontWeight: '300', // Light
                fontSize: '0.875rem', // 14px
                lineHeight: '1.25rem', // 20px
                letterSpacing: '-0.02em' // -2%
            }
        },
        xsmall: {
            value: {
                fontFamily: 'Stokke',
                fontWeight: '300', // Light
                fontSize: '0.75rem', // 12px
                lineHeight: '1.125rem', // 18px
                letterSpacing: '-0.01em' // -1%
            }
        }
    },
    // label styles are not fluid - note we are using `labels` plural to avoid conflict with chakra's `label` text style
    labels: {
        medium: {
            value: {
                ...labelMedium
            }
        },
        mediumCaps: {
            value: {
                ...labelMedium,
                textTransform: 'uppercase'
            }
        }
    }
})
